import { useMemo } from 'react';

import { PlatformType } from '@/react/portainer/environments/types';
import { getPlatformIconByPlatform } from '@/react/portainer/environments/utils/get-platform-icon';

import {
  SortableGroup,
  SortableList,
  SortOption,
} from '@@/SortableList/SortableList';
import { DropdownOption } from '@@/DropdownMenu/DropdownMenu';
import { useSortableListState } from '@@/SortableList/sortable-list.store';

import { useGroups } from '../../queries';
import { EnvironmentGroup } from '../../types';
import { isUngoverned } from '../../utils/getPlatformLabel';

import { EnvironmentGroupRow } from './EnvironmentGroupRow';

type SortKey = 'Name' | 'Platform' | 'Governance';

const SORT_OPTIONS: SortOption<SortKey>[] = [
  { key: 'Name', label: 'Name' },
  { key: 'Platform', label: 'Platform', grouped: true },
  { key: 'Governance', label: 'Governance', grouped: true },
];

export function EnvironmentGroupsTable() {
  const groupsQuery = useGroups({ size: true });
  const tableState = useSortableListState('environment_groups', 'Name');

  const sorted = useMemo(() => {
    const data = groupsQuery.data ?? [];
    const governed = data.filter((g) => !isUngoverned(g));
    const ungoverned = data.filter((g) => isUngoverned(g));
    const sortedGoverned = [...governed].sort((a, b) => {
      const cmp = a.Name.localeCompare(b.Name);
      return tableState.sortBy?.desc ? -cmp : cmp;
    });
    return [...sortedGoverned, ...ungoverned];
  }, [groupsQuery.data, tableState.sortBy?.desc]);

  const groupOptions = useMemo(() => buildGroupOptions(sorted), [sorted]);

  const filtered = useMemo(
    () =>
      applyFilters(
        sorted,
        tableState.search,
        tableState.groupBy,
        tableState.groupFilter
      ),
    [sorted, tableState.search, tableState.groupBy, tableState.groupFilter]
  );

  const pageItems = useMemo(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(filtered.length / tableState.pageSize)
    );
    const safePage = Math.min(tableState.page, totalPages - 1);
    const start = safePage * tableState.pageSize;
    return filtered.slice(start, start + tableState.pageSize);
  }, [filtered, tableState.page, tableState.pageSize]);

  const groups = useMemo(() => buildGroups(pageItems), [pageItems]);

  return (
    <SortableList
      tableState={tableState}
      sortOptions={SORT_OPTIONS}
      groupOptions={groupOptions}
      groups={groups}
      totalCount={filtered.length}
      renderItem={(group) => <EnvironmentGroupRow group={group} />}
      getItemKey={(group) => group.Id}
      isLoading={groupsQuery.isLoading}
      searchPlaceholder="Filter groups..."
      emptyMessage={
        tableState.search
          ? 'No groups match your search'
          : 'No environment groups found'
      }
      data-cy="environment-groups-list"
    />
  );
}

function applyFilters(
  items: EnvironmentGroup[],
  search: string,
  groupBy: string | null,
  groupFilter: string | null
): EnvironmentGroup[] {
  let result = items;

  if (search) {
    const term = search.toLowerCase();
    result = result.filter(
      (g) =>
        g.Name.toLowerCase().includes(term) ||
        g.Description?.toLowerCase().includes(term)
    );
  }

  if (groupBy === 'Platform' && groupFilter) {
    result = result.filter((g) => {
      const info = g.TypeInfo;
      if (!info) return false;
      if (groupFilter === 'Docker') return info.Docker > 0;
      if (groupFilter === 'Kubernetes') return info.Kubernetes > 0;
      if (groupFilter === 'Podman') return info.Podman > 0;
      return false;
    });
  }

  if (groupBy === 'Governance' && groupFilter) {
    if (groupFilter === 'Ungoverned') {
      result = result.filter((g) => isUngoverned(g));
    } else if (groupFilter === 'Policy Attached') {
      result = result.filter(
        (g) => !isUngoverned(g) && (g.Policies?.length ?? 0) > 0
      );
    }
  }

  return result;
}

function buildGroups(
  items: EnvironmentGroup[]
): SortableGroup<EnvironmentGroup>[] {
  const governed = items.filter((g) => !isUngoverned(g));
  const ungoverned = items.filter((g) => isUngoverned(g));
  const result: SortableGroup<EnvironmentGroup>[] = [];
  if (governed.length) {
    result.push({ key: 'governed', label: 'Groups', items: governed });
  }
  if (ungoverned.length) {
    result.push({ key: 'ungoverned', label: 'Ungoverned', items: ungoverned });
  }
  return result;
}

function buildGroupOptions(
  groups: EnvironmentGroup[]
): Record<string, DropdownOption[]> {
  const dockerCount = groups.reduce(
    (sum, g) => sum + (g.TypeInfo?.Docker ?? 0),
    0
  );
  const kubernetesCount = groups.reduce(
    (sum, g) => sum + (g.TypeInfo?.Kubernetes ?? 0),
    0
  );
  const podmanCount = groups.reduce(
    (sum, g) => sum + (g.TypeInfo?.Podman ?? 0),
    0
  );

  const governedCount = groups.filter(
    (g) => !isUngoverned(g) && (g.Policies?.length ?? 0) > 0
  ).length;
  const ungovernedCount = groups.filter((g) => isUngoverned(g)).length;

  const platformOptions: DropdownOption[] = [
    {
      key: 'Docker',
      label: 'Docker',
      count: dockerCount,
      icon: getPlatformIconByPlatform(PlatformType.Docker, 'md'),
    },
    {
      key: 'Kubernetes',
      label: 'Kubernetes',
      count: kubernetesCount,
      icon: getPlatformIconByPlatform(PlatformType.Kubernetes, 'md'),
    },
    {
      key: 'Podman',
      label: 'Podman',
      count: podmanCount,
      icon: getPlatformIconByPlatform(PlatformType.Podman, 'md'),
    },
  ].filter((o) => (o.count ?? 0) > 0);

  return {
    Platform: platformOptions,
    Governance: [
      {
        key: 'Policy Attached',
        label: 'Policy Attached',
        count: governedCount,
        icon: (
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-success-8" />
        ),
      },
      {
        key: 'Ungoverned',
        label: 'Ungoverned',
        count: ungovernedCount,
        icon: (
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-warning-8" />
        ),
      },
    ].filter((o) => (o.count ?? 0) > 0),
  };
}
